import Swup from "swup";
import SwupA11yPlugin from "@swup/a11y-plugin";
import SwupHeadPlugin from "@swup/head-plugin";
import SwupScriptsPlugin from "@swup/scripts-plugin";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

import { closeNav } from "./modules/navigation.js";
import { initSwipers } from "./modules/swiper.js";
import { initSlideToggles } from "./modules/slideToggle.js";

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

// Configure elements to animate on scroll
const selectors = [
  ".button",
  ".richtext",
  ".swiper-slide",
  ".filter-wrapper",
  ".controls",
  ".card",
  ".slide-toggle",
  "figure",
  "picture",
  "img",
  "svg",
  "video",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "ul",
  "ol",
  "blockquote",
  "form",
  "table",
];

const exclusions = [".background-image"];

const selectorString = selectors.join(",");
const exclusionString = exclusions.join(",");

function getAnimationTargets() {
  const main = document.querySelector("main");
  if (!main) return [];

  const matches = [...main.querySelectorAll(selectorString)];

  return matches.filter((el) => {
    // Ignore excluded elements
    if (el.matches(exclusionString)) {
      return false;
    }

    // Ignore anything inside an excluded parent
    if (el.closest(exclusionString)) {
      return false;
    }

    // Ignore elements whose ancestor is already a target
    let parent = el.parentElement;

    while (parent && parent !== main) {
      if (parent.matches(selectorString) && !parent.matches(exclusionString)) {
        return false;
      }

      parent = parent.parentElement;
    }

    return true;
  });
}

function revealInitiallyVisible(targets) {
  targets.forEach((el) => {
    const rect = el.getBoundingClientRect();

    if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
      gsap.to(el, {
        autoAlpha: 1,
        y: 0,
      });
    }
  });
}

// Initialize scrolling animations
function initAnimations() {
  const targets = getAnimationTargets();

  gsap.set(targets, {
    autoAlpha: 0,
    y: 24,
  });

  revealInitiallyVisible(targets);

  ScrollTrigger.batch(targets, {
    start: "top 90%",
    once: true,
    onEnter: (batch) =>
      gsap.to(batch, {
        autoAlpha: 1,
        y: 0,
        stagger: 0.12,
        duration: 0.7,
        ease: "power2.out",
      }),
  });

  ScrollTrigger.refresh();
}

// Handle filter toggle state
let isFilterOpen = false;

function setFilterButtonText(button, isOpen) {
  button.textContent = isOpen ? "Hide filters" : "Show filters";
}

function setFilterPanelState(panel, isOpen, animate = true) {
  const props = isOpen
    ? { height: "auto", opacity: 1, duration: 0.4, ease: "power2.out" }
    : { height: 0, opacity: 0, duration: 0.3, ease: "power2.in" };

  if (animate) {
    gsap.to(panel, props);
    return;
  }

  const { duration, ease, ...setProps } = props;
  gsap.set(panel, setProps);
}

function initFilterToggle(root = document) {
  const panel =
    root.querySelector?.(".filters") || document.querySelector(".filters");
  const button =
    root.querySelector?.(".filter-toggle") ||
    document.querySelector(".filter-toggle");

  if (!panel || !button) return;
  setFilterButtonText(button, isFilterOpen);
  if (button.dataset.filterToggleBound === "true") return;

  button.dataset.filterToggleBound = "true";

  button.addEventListener("click", () => {
    isFilterOpen = !isFilterOpen;
    setFilterPanelState(panel, isFilterOpen, true);
    setFilterButtonText(button, isFilterOpen);
  });
}

const initAll = () => {
  if (!prefersReducedMotion) {
    initAnimations();
  }

  initSlideToggles();
  initSwipers();
  initFilterToggle();
};

initAll();

const swupContainer = document.querySelector("#swup");

if (swupContainer) {
  const swup = new Swup({
    containers: ["#swup"],
    linkToSelf: "navigate",
    plugins: [
      new SwupHeadPlugin({
        awaitAssets: true,
        persistTags: true,
      }),
      new SwupA11yPlugin(),
      new SwupScriptsPlugin(),
    ],
  });

  swup.hooks.on("page:view", () => {
    isFilterOpen = false;

    initAll();

    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  });

  swup.hooks.on("visit:start", () => {
    closeNav();
    ScrollTrigger.killAll();
  });
}
